import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror';
import type { ID } from '@proofroom/schema';
import { YJS_WS_PATH } from '@proofroom/schema';
import type { PMNode } from '../model/pmJson';

export const FRAGMENT = 'prosemirror';
export const roomName = (draftId: ID, storyId: ID): string => `draft:${draftId}:story:${storyId}`;

export interface Room {
  name: string;
  storyId: ID;
  doc: Y.Doc;
  fragment: Y.XmlFragment;
  provider?: WebsocketProvider;
  /** resolves once the server state has been received (immediately when local only) */
  synced: Promise<void>;
  /** true when the room lives only in this tab (fixture mode) and must be seeded from the snapshot */
  local: boolean;
  status: 'local' | 'connecting' | 'connected' | 'disconnected';
  onStatus(cb: (s: Room['status']) => void): () => void;
  destroy(): void;
}

function wsBase(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}${YJS_WS_PATH}`;
}

/**
 * One Y.Doc per story per draft, kept alive across editor mounts so a story edited and then closed
 * can still be materialised for "Send to designer". Local only in fixture mode.
 */
export class RoomRegistry {
  private rooms = new Map<string, Room>();
  constructor(private draftId: ID, private local: boolean) {}

  get(storyId: ID): Room {
    const name = roomName(this.draftId, storyId);
    let room = this.rooms.get(name);
    if (room) return room;
    const doc = new Y.Doc();
    const fragment = doc.getXmlFragment(FRAGMENT);
    const listeners = new Set<(s: Room['status']) => void>();
    let status: Room['status'] = this.local ? 'local' : 'connecting';
    const setStatus = (s: Room['status']) => { status = s; room!.status = s; for (const l of listeners) l(s); };
    let provider: WebsocketProvider | undefined;
    let synced: Promise<void> = Promise.resolve();
    if (!this.local) {
      provider = new WebsocketProvider(wsBase(), name, doc, { connect: true, maxBackoffTime: 5000 });
      // the server seeds the room from the draft's Story on first open, so wait for its state
      synced = new Promise<void>((resolve) => { provider!.once('sync', () => resolve()); });
      provider.on('status', (ev: { status: string }) => setStatus(ev.status === 'connected' ? 'connected' : ev.status === 'connecting' ? 'connecting' : 'disconnected'));
      provider.on('connection-close', () => setStatus('disconnected'));
    }
    room = {
      name, storyId, doc, fragment, provider, synced, status, local: this.local,
      onStatus: (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
      destroy: () => { provider?.destroy(); doc.destroy(); this.rooms.delete(name); },
    };
    this.rooms.set(name, room);
    return room;
  }

  has(storyId: ID): boolean { return this.rooms.has(roomName(this.draftId, storyId)); }

  /** Current document of a room as ProseMirror JSON (works without an editor mounted). */
  json(storyId: ID): PMNode | undefined {
    const room = this.rooms.get(roomName(this.draftId, storyId));
    if (!room || room.fragment.length === 0) return undefined;
    return yXmlFragmentToProsemirrorJSON(room.fragment) as PMNode;
  }

  destroyAll(): void {
    for (const r of [...this.rooms.values()]) r.destroy();
    this.rooms.clear();
  }
}
